<script setup lang="ts">
import { AssetType, SellingMethod } from '~/apiClient';
import type { BaseAccordionItem } from '~/components/Base/BaseAccordion.vue';

// COMPOSABLES
const { tt } = useTypedI18n();
const sellingMethod = useSlpSellingMethod();
const {
    // Filters
    busType,
    platformBusClass,
    euroNorm,
    firstRegistrationYearFrom,
    firstRegistrationYearTo,
    fuel,
    kmFrom,
    kmTo,
    lengthFrom,
    lengthTo,
    priceFrom,
    priceTo,
    seatsFrom,
    seatsTo,
    aState,
    dsState,

    // OPTIONS
    busTypeOptions,
    platformBusClassOptions,
    euroNormOptions,
    firstRegistrationYearFromOptions,
    firstRegistrationYearToOptions,
    fuelOptions,
    kmFromOptions,
    kmToOptions,
    lengthFromOptions,
    lengthToOptions,
    priceFromOptions,
    priceToOptions,
    seatsFromOptions,
    seatsToOptions,
    aStateOptions,
    dsStateOptions,
} = useSlpParams(sellingMethod);
const { formatSlpRoute } = useSlpRouteUtils();

// DATA
const items = computed<BaseAccordionItem[]>(() => [
    { label: tt(`slp.${sellingMethod.value}.filters.state.title`), slot: 'state', defaultOpen: true },
    { label: tt('slp.filters.price'), slot: 'price' },
    { label: tt('slp.filters.scrapping.title'), slot: 'scrapping' },
    { label: tt('slp.filters.busType'), slot: 'busType' },
    { label: tt('slp.filters.class'), slot: 'class' },
    { label: tt('slp.filters.brand'), slot: 'brand' },
    { label: tt('slp.filters.euroNorm'), slot: 'euro-norm' },
    { label: tt('slp.filters.firstRegistrationYear'), slot: 'first-firstRegistrationYear-year' },
    { label: tt('slp.filters.km'), slot: 'km' },
    { label: tt('slp.filters.fuel'), slot: 'fuel' },
    { label: tt('slp.filters.length'), slot: 'length' },
    { label: tt('slp.filters.seats'), slot: 'seats' },
]);
</script>

<template>
    <BaseAccordion :items="items" type="multiple">
        <template #state>
            <template v-if="sellingMethod === SellingMethod.Auction">
                <UCheckboxGroup v-model="aState" class="pb-3.5 lg:px-1" :items="aStateOptions" size="xl" />
            </template>
            <template v-if="sellingMethod === SellingMethod.DirectSale">
                <UCheckboxGroup v-model="dsState" class="pb-3.5 lg:px-1" :items="dsStateOptions" size="xl" />
            </template>
        </template>
        <template #price>
            <div class="flex flex-col gap-3 pb-3.5 sm:flex-row sm:gap-8 lg:flex-col lg:gap-3 lg:pl-1">
                <SlpFiltersFieldFromTo
                    v-model:from="priceFrom"
                    v-model:to="priceTo"
                    :items-from="priceFromOptions"
                    :items-to="priceToOptions"
                    :any-label="tt('slp.filters.priceAnyOption')"
                />
            </div>
        </template>
        <template #scrapping>
            <SlpFiltersFieldScrapping class="pb-3.5" />
        </template>
        <template #busType>
            <UCheckboxGroup v-model="busType" class="pb-3.5 lg:px-1" :items="busTypeOptions" size="xl" />
        </template>
        <template #class>
            <UCheckboxGroup v-model="platformBusClass" class="pb-3.5 lg:px-1" :items="platformBusClassOptions" size="xl" />
            <NuxtLinkLocale
                v-for="_classOption in platformBusClassOptions"
                :key="_classOption.value"
                :to="formatSlpRoute({ sellingMethod, assetType: AssetType.Bus, platformClass: _classOption.value })"
                class="hidden"
            >
                {{ _classOption.label }}
            </NuxtLinkLocale>
        </template>
        <template #brand>
            <div class="flex flex-col gap-3 pb-3.5 sm:flex-row sm:gap-5 lg:flex-col lg:gap-3 lg:px-1">
                <SlpFiltersFieldBrandModelVersion />
            </div>
        </template>
        <template #euro-norm>
            <UCheckboxGroup v-model="euroNorm" class="pb-3.5 lg:px-1" :items="euroNormOptions" size="xl" />
        </template>
        <template #first-firstRegistrationYear-year>
            <div class="flex flex-col gap-3 pb-3.5 sm:flex-row sm:gap-8 lg:flex-col lg:gap-3 lg:px-1">
                <SlpFiltersFieldFromTo
                    v-model:from="firstRegistrationYearFrom"
                    v-model:to="firstRegistrationYearTo"
                    :items-from="firstRegistrationYearFromOptions"
                    :items-to="firstRegistrationYearToOptions"
                    :from-label="tt('slp.filters.firstRegistrationYearFrom')"
                    :to-label="tt('slp.filters.firstRegistrationYearTo')"
                    :any-label="tt('slp.filters.firstRegistrationYearAnyOption')"
                />
            </div>
        </template>
        <template #km>
            <div class="flex flex-col gap-3 pb-3.5 sm:flex-row sm:gap-8 lg:flex-col lg:gap-3 lg:pl-1">
                <SlpFiltersFieldFromTo
                    v-model:from="kmFrom"
                    v-model:to="kmTo"
                    :items-from="kmFromOptions"
                    :items-to="kmToOptions"
                    :any-label="tt('slp.filters.kmAnyOption')"
                />
            </div>
        </template>
        <template #fuel>
            <UCheckboxGroup v-model="fuel" class="pb-3.5 lg:px-1" :items="fuelOptions" size="xl" />
        </template>
        <template #length>
            <div class="flex flex-col gap-3 pb-3.5 sm:flex-row sm:gap-8 lg:flex-col lg:gap-3 lg:px-1">
                <SlpFiltersFieldFromTo
                    v-model:from="lengthFrom"
                    v-model:to="lengthTo"
                    :items-from="lengthFromOptions"
                    :items-to="lengthToOptions"
                    :any-label="tt('slp.filters.lengthAnyOption')"
                />
            </div>
        </template>
        <template #seats>
            <div class="flex flex-col gap-3 pb-3.5 sm:flex-row sm:gap-8 lg:flex-col lg:gap-3 lg:pl-1">
                <SlpFiltersFieldFromTo v-model:from="seatsFrom" v-model:to="seatsTo" :items-from="seatsFromOptions" :items-to="seatsToOptions" />
            </div>
        </template>
    </BaseAccordion>
</template>
